import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Sparkles, HelpCircle, ChevronLeft, ChevronRight, X,
  Coins, PlayCircle, Target, Watch, BellRing, Star, Flame, Footprints, Shuffle, Trophy, Shield, LayoutGrid, Compass,
} from 'lucide-react';

// Bump WHATS_NEW_VERSION and add a release on top when you ship something worth
// showing. Who already saw an older release sees only the newer ones; a brand
// new user sees everything once. "Rivedi le novità" in Aiuto replays it all.
export const WHATS_NEW_VERSION = '2026-10-21';

const RELEASES = [
  {
    version: '2026-10-21',
    slides: [
      {
        icon: Shield,
        title: 'Card nuove',
        body: 'Le card hanno un bordo nuovo a punte, la foto senza riquadro e il logo LeagueHub in basso. Abilità e forma si leggono a colpo d’occhio: stelle per l’abilità, fiamme per la forma.',
        accent: 'from-amber-500 via-orange-500 to-red-600',
        cta: 'Avanti',
      },
    ],
  },
  {
    version: '2026-10-11b',
    slides: [
      {
        icon: Shield,
        title: 'Ogni giocatore ha la sua card',
        body: 'Nella lega, sezione Membri: ogni giocatore ha la sua card in stile Ultimate Team, con voto, ruolo, piede e attributi. Il colore segue la forma, da Gelido a In fiamme. Con l’MVP dell’ultima partita o tre vittorie di fila si sblocca un’edizione speciale.',
        accent: 'from-amber-500 via-orange-500 to-red-600',
        cta: 'Avanti',
      },
      {
        icon: LayoutGrid,
        title: 'In campo con le card',
        body: 'Le card sono anche nella partita: titolari, disposizione in campo (difensori vicino alla porta, attaccanti verso il centro) e squadre proposte dal generatore. Nel Profilo trovi la tua e imposti piede forte e attitudine.',
        accent: 'from-emerald-500 via-green-600 to-teal-700',
        cta: 'Avanti',
      },
      {
        icon: Compass,
        title: 'Ti muovi più veloce',
        body: 'Freccia indietro in alto nelle pagine interne, tutte le sezioni della lega in una barra che scorre, salti rapidi nella pagina della partita e il Profilo nella barra in basso.',
        accent: 'from-sky-500 via-blue-600 to-indigo-700',
        cta: 'Avanti',
      },
    ],
  },
  {
    version: '2026-09-22',
    slides: [
      {
        icon: Sparkles,
        title: 'La tua scheda giocatore',
        body: 'Ogni giocatore ha ora una scheda in stile FC: livello, forma, piede preferito, piede debole e caratteristiche. La apri toccando un nome nella lista membri o dal tuo Profilo.',
        accent: 'from-blue-600 via-indigo-600 to-violet-600',
        cta: 'Fammi vedere',
      },
      {
        icon: Flame,
        title: 'La forma si calcola da sola',
        body: 'Le fiammelle non le imposta più nessuno a mano: vengono dalle tue ultime 5 partite, dalle vittorie, dai voti MVP che ricevi. Se stai fermo un po’, la forma scende piano verso il neutro. L’admin può forzarla in casi particolari.',
        accent: 'from-orange-500 via-red-500 to-rose-600',
        cta: 'Avanti',
      },
      {
        icon: Star,
        title: 'Mezze stelle, piede e piede debole',
        body: 'Livello e forma ora vanno a passi di mezza stella. Nel Profilo imposti se sei destro, mancino o ambidestro e quanto vali col piede debole: conta nelle squadre.',
        accent: 'from-amber-500 via-yellow-500 to-orange-500',
        cta: 'Avanti',
      },
      {
        icon: Shuffle,
        title: 'Genera squadre, versione 2',
        body: 'Il generatore usa livello, forma, piede debole e caratteristiche, e distribuisce i mancini tra le due squadre. Nella proposta vedi stelle, fiamme e piede di ognuno.',
        accent: 'from-emerald-500 via-teal-500 to-cyan-500',
        cta: 'Avanti',
      },
      {
        icon: BellRing,
        title: 'Notifiche sul telefono',
        body: 'Nuova partita nella lega, promemoria dell’organizzatore, squadre confermate, risultato: arrivano come notifica sul telefono anche con l’app chiusa. Basta dare il permesso al primo avvio.',
        accent: 'from-sky-500 via-blue-500 to-indigo-600',
        cta: 'Avanti',
      },
      {
        icon: Trophy,
        title: 'MVP: niente autovoti',
        body: 'Nella votazione del migliore in campo non puoi più scegliere te stesso. Il voto resta segreto e si chiude quando hanno votato tutti.',
        accent: 'from-fuchsia-500 via-pink-500 to-rose-500',
        cta: 'Avanti',
      },
    ],
  },
  {
    version: '2026-05-22',
    slides: [
      {
        icon: PlayCircle,
        title: 'Modalità Partita Live',
        body: 'Dal dettaglio della partita, il bottone verde "Modalità Live": cronometro, timer rotazione portiere ogni 5 minuti con sveglia, e un +1 per ogni titolare per segnare i gol in tempo reale.',
        accent: 'from-emerald-500 via-teal-500 to-cyan-500',
        cta: 'Avanti',
      },
      {
        icon: Target,
        title: 'Classifica cannonieri',
        body: 'Tab "Cannonieri" nelle leghe e nelle competizioni: podio e classifica completa, aggiornati con i gol che registri nelle partite.',
        accent: 'from-rose-500 via-pink-500 to-fuchsia-500',
        cta: 'Avanti',
      },
      {
        icon: Coins,
        title: 'Pagamenti partita',
        body: 'Ogni titolare segna se ha pagato online o pagherà al campo, anche per i compagni. La quota la imposti creando la partita; vedi al volo chi ha pagato e cosa manca.',
        accent: 'from-amber-500 via-orange-500 to-rose-500',
        cta: 'Avanti',
      },
      {
        icon: Watch,
        title: 'LeagueHub sull’Apple Watch',
        body: 'Gestisci la partita dal polso: timer del portiere con sveglia e gol rapidi con un tap.',
        accent: 'from-slate-600 via-slate-700 to-zinc-800',
        cta: 'Avanti',
      },
    ],
  },
];

const GUIDE_SLIDE = {
  icon: HelpCircle,
  title: 'Hai un dubbio? C’è la guida',
  body: 'Risposte semplici per giocatori, organizzatori e gestori di strutture. Sempre a portata di click dal menu Aiuto, dove puoi anche rivedere queste novità.',
  accent: 'from-cyan-500 via-blue-500 to-indigo-500',
  cta: 'Vai alla guida',
  action: 'help',
};

const LAST_SEEN_KEY = 'lh_whats_new_last_seen';
const LEGACY_SEEN_KEY = 'lh_whats_new_seen_2026-05-22.2';
export const SHOW_WHATS_NEW_EVENT = 'lh-show-whats-new';

/** Slides a viewer who last saw `lastSeen` still has to see (all, if never). */
export const slidesFor = (lastSeen) => {
  const releases = lastSeen ? RELEASES.filter((r) => r.version > lastSeen) : RELEASES;
  if (!releases.length) return [];
  return [...releases.flatMap((r) => r.slides), GUIDE_SLIDE];
};

const readLastSeen = () => {
  try {
    const seen = window.localStorage.getItem(LAST_SEEN_KEY);
    if (seen) return seen;
    // Users who dismissed the previous modal saw the May release.
    if (window.localStorage.getItem(LEGACY_SEEN_KEY)) return '2026-05-22';
  } catch (e) {
    // localStorage may be blocked
  }
  return null;
};

/** Open the modal from anywhere (e.g. Aiuto → "Rivedi le novità"). */
export const showWhatsNew = () => window.dispatchEvent(new Event(SHOW_WHATS_NEW_EVENT));

export const WhatsNewModal = () => {
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const pending = slidesFor(readLastSeen());
    if (pending.length) {
      setSlides(pending);
      const t = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const replay = () => {
      setSlides(slidesFor(null));
      setIndex(0);
      setOpen(true);
    };
    window.addEventListener(SHOW_WHATS_NEW_EVENT, replay);
    return () => window.removeEventListener(SHOW_WHATS_NEW_EVENT, replay);
  }, []);

  const markSeen = () => {
    try {
      window.localStorage.setItem(LAST_SEEN_KEY, WHATS_NEW_VERSION);
    } catch (e) {
      // ignore
    }
  };

  const handleClose = () => {
    markSeen();
    setOpen(false);
  };

  const handleNext = () => {
    const slide = slides[index];
    if (index === slides.length - 1) {
      markSeen();
      setOpen(false);
      if (slide.action === 'help') navigate('/help');
      return;
    }
    setIndex((i) => Math.min(slides.length - 1, i + 1));
  };

  const handlePrev = () => setIndex((i) => Math.max(0, i - 1));

  if (!slides.length) return null;
  const slide = slides[Math.min(index, slides.length - 1)];
  const Icon = slide.icon;
  const isLast = index === slides.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : handleClose())}>
      <DialogContent className="max-w-md p-0 overflow-hidden gap-0 border-0" data-testid="whats-new">
        {/* Visual hero */}
        <div className={`relative bg-gradient-to-br ${slide.accent} px-6 pt-10 pb-8 text-white`}>
          <div className="pointer-events-none absolute -top-16 -right-16 w-48 h-48 rounded-full bg-white/15 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-white/15 blur-3xl" />

          <button
            type="button"
            onClick={handleClose}
            aria-label="Chiudi"
            className="absolute top-3 right-3 h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur flex items-center justify-center transition-colors"
          >
            <X className="h-4 w-4" />
          </button>

          <div className="relative flex flex-col items-center text-center">
            <div className="h-20 w-20 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center shadow-2xl mb-4">
              <Icon className="h-10 w-10" />
            </div>
            <span className="text-[10px] uppercase tracking-widest text-white/80 mb-1">
              Novità · {index + 1} di {slides.length}
            </span>
            <DialogTitle className="text-2xl font-bold drop-shadow-md">
              {slide.title}
            </DialogTitle>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5 bg-card">
          <DialogDescription className="text-base text-foreground/80 leading-relaxed text-center min-h-[60px]">
            {slide.body}
          </DialogDescription>

          {/* Dots */}
          <div className="flex items-center justify-center gap-1.5 mt-6">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`Vai a slide ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${
                  i === index ? 'w-6 bg-primary' : 'w-1.5 bg-muted hover:bg-muted-foreground/40'
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 mt-6">
            <Button
              type="button"
              variant="ghost"
              onClick={handlePrev}
              disabled={index === 0}
              className="px-3"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Indietro
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={handleClose}
              className="ml-auto text-muted-foreground"
            >
              Salta
            </Button>
            <Button type="button" onClick={handleNext} className="min-w-[110px]">
              {slide.cta}
              {!isLast && <ChevronRight className="h-4 w-4 ml-1" />}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WhatsNewModal;
